<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>

    <div class="sl-main">

        <div v-if="!loading" class="sl-page sl-page--full sl-page--paddless">

            <div class="m-4 text-xl text-primary-500">
                {{ $t('return.title') }}
            </div>

            <!-- Step 1 : select lens order to retrieve -->
            <template v-if="(!leftReturn && !rightReturn) || orderIdentifierFormError">

                <!-- order identifier form -->
                <order-identifier-form
                    v-model:order-identifier="orderIdentifier"
                    v-model:return-both-lenses="returnBothLenses"
                    :error="orderIdentifierFormError"
                    @next="displayReturnForm()"
                />

                <!-- "Next" Button -->
                <div class="flex w-full items-center justify-center">
                    <button class="lsn-btn" :class="nextButtonClass" @click="displayReturnForm()">
                        {{ $t('common.next') }}
                    </button>
                </div>

                <!-- <return-basket /> -->

            </template>

            <!-- Step 2 : return form -->
            <template v-else-if="formDual">

                <!-- Back button -->
                <div class="text-primary-500 ml-4 mb-2" @click="displayOrderIdentifierForm()">
                    &larr; {{ $t('common.return') }}
                </div>

                <form-dual
                    :form-dual="formDual"
                    @update="template"
                />

                <div v-if="formDual.hasErrors()" class="flex mx-3 my-4 p-3 bg-red-100">
                    <div class="w-1/2 text-red-600 text-center text-sm">
                        {{ formDual.getError('left') }}
                    </div>
                    <div class="w-1/2 text-red-600 text-center text-sm">
                        {{ formDual.getError('right') }}
                    </div>
                </div>

                <!-- "Submit" Button -->
                <div class="my-6">
                    <div class="flex justify-center w-full">
                        <button class="lsn-btn lsn-btn--primary sl-order__submit" :tabindex="3" @click="save">
                            {{ $t('return.lenses.save-btn') }}
                        </button>
                    </div>
                </div>

            </template>
        </div>

        <!-- Loader (while loading) -->
        <div v-if="loading" class="mt-8 mx-auto text-center">
            <img class="inline" src="/img/loader-5.gif">
        </div>

        <!-- Re-order: not same entity modal warning -->
        <choose-entity-modal
            :is-open="showChooseEntityModal"
            :ret="leftReturn ?? rightReturn"
            @close="closeEntitySwitchAndReset"
            @next="dispatch"
        />

        <!-- Dispatch Error -->
        <teleport to="body">
            <lsn-modal :is-open="dispatchError !== null">
                <div class="sl-prompt sl-prompt--danger text-center">
                    <div class="sl-prompt__contents">
                        <div class="sl-prompt__title">
                            {{ dispatchErrorTitle }}
                        </div>

                        <div class="sl-prompt__text">
                            {{ dispatchErrorText }}
                        </div>
                    </div>

                    <div class="sl-prompt__actions">
                        <button class="sl-prompt__button sl-prompt__button--white" @click="dispatchError = null">
                            {{ $t('common.close') }}
                        </button>
                    </div>
                </div>
            </lsn-modal>
        </teleport>

    </div>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

import {
    get,
} from 'lodash-es';

// Helper Objects
import LensReturnUrlManager from './LensReturnUrlManager';

// Components
import OrderIdentifierForm from './OrderIdentifierForm';
import FormDual from '@/components/forms/form-dual/FormDual.vue';
import ChooseEntityModal from './ChooseEntityModal';
// import ReturnBasket from './ReturnBasket';

// Chain of responsibility handlers: "dispatch"
import ResetErrors from './handlers/ResetErrors';
import FetchOrderByReference from './handlers/FetchOrderByReference';
import FetchOrderByLensSn from './handlers/FetchOrderByLensSn';
import FetchOrderByLensOemNumber from './handlers/FetchOrderByLensOemNumber';
import CheckReturnOrStop from './handlers/CheckReturnOrStop';
import FetchPairedOrderByReference from './handlers/FetchPairedOrderByReference';
import CheckAlreadyShipped from './handlers/CheckAlreadyShipped';
import CheckNotAlreadyReturned from './handlers/CheckNotAlreadyReturned';
import ValidateReturnEntitiesConsistency from './handlers/ValidateReturnEntitiesConsistency';
import TemplateReturnQuestionnaire from './handlers/TemplateReturnQuestionnaire';
import SetReturnQuestionnaireValues from './handlers/SetReturnQuestionnaireValues';
import CreateFormDual from './handlers/CreateFormDual';
import LinkReturnQuestionnairesToFormDual from './handlers/LinkReturnQuestionnairesToFormDual';
import RenderFormDual from './handlers/RenderFormDual';

// Chain of responsibility handlers: "add to return basket"
import ValidateReturns from './handlers/ValidateReturns';
import PriceReturns from './handlers/PriceReturns';
import CreateReturns from './handlers/CreateReturns';
import NavigateToReturnConfirmation from './handlers/NavigateToReturnConfirmation';
import SetReturnPairId from './handlers/SetReturnPairId';
import AddToReturnBasket from './handlers/AddToReturnBasket';
import NavigateToBasket from './handlers/NavigateToBasket';

// Chain of responsibility maps (used as queues)
const dispatchQueue = new Map();
const addToReturnBasketQueue = new Map();

export default
{
    name: 'ReturnLenses',

    // ------------------------------------------------------------------------- COMPONENTS

    components:
    {
        OrderIdentifierForm,
        FormDual,
        ChooseEntityModal,
        // ReturnBasket,
    },

    // ------------------------------------------------------------------------- DATA

    data()
    {
        return {
            // Status
            loading: false,

            // URL
            lensReturnUrlManager: null,

            // Other
            showChooseEntityModal: false,

            // Lenses
            orderIdentifier:  null,
            returnBothLenses: true,

            // Errors
            orderIdentifierFormError: null,
            dispatchError:            null,
        };
    },

    // ------------------------------------------------------------------------- COMPUTED PROPERTIES

    computed:
    {
        /**
         * Used in handlers!
         */
        cEntity()
        {
            return this.$store.state.account.cEntity;
        },

        isNextButtonActive()
        {
            return this.orderIdentifier;
        },

        nextButtonClass()
        {
            return this.isNextButtonActive
                ? 'lsn-btn--primary sl-order__submit'
                : 'lsn-btn--gray-outline lsn-btn--disabled';
        },

        leftReturn:
        {
            get()
            {
                return this.$store.state.lensReturn.returns.left;
            },
            set(leftReturn)
            {
                this.$store.commit('lensReturn/setReturn', {
                    ret:  leftReturn,
                    side: 'left',
                });
            },
        },

        rightReturn:
        {
            get()
            {
                return this.$store.state.lensReturn.returns.right;
            },
            set(rightReturn)
            {
                this.$store.commit('lensReturn/setReturn', {
                    ret:  rightReturn,
                    side: 'right',
                });
            },
        },

        formDual()
        {
            return this.$store.state.lensReturn.formDual;
        },
    },

    // ------------------------------------------------------------------------- WATCHERS

    watch:
    {
        'leftReturn.return_reason_code': function(reasonCode)
        {
            this.formDual.resetError('left');

            if(!reasonCode)
            {
                this.leftReturn.resetPrice();
            }
            else
            {
                this.leftReturn.fetchPrice().catch(error =>
                {
                    if(error.response.status == 422)
                    {
                        this.formDual.setErrors(error.response.errors[0], 'left');
                    }
                });
            }
        },

        'rightReturn.return_reason_code': function(reasonCode)
        {
            this.formDual.resetError('right');

            if(!reasonCode)
            {
                this.rightReturn.resetPrice();
            }
            else
            {
                this.rightReturn.fetchPrice().catch(error =>
                {
                    if(error.response.status == 422)
                    {
                        this.formDual.setErrors(error.response.errors[0], 'right');
                    }
                });
            }
        },

        '$route.params'(newParams, oldParams)
        {
            // Avoid dispatch when adding to basket
            if(!['return-lenses', 'edit-return-lenses'].includes(this.$route.name))
            {
                console.log('route has changed: dispatch avoided.');
                return;
            }

            this.lensReturnUrlManager.setFromRoute(this.$route);

            // Exception when user clicks on "New Return" on the main menu
            if(!newParams.lenses)
            {
                this.orderIdentifier = this.$route.query.order_identifier ?? null;
                this.returnBothLenses = this.$route.query.return_both_lenses ?? null;

                // Reset both sides
                this.$store.commit('lensReturn/reset');
            }
            else
            {
                this.dispatch();
            }
        },
    },

    // ------------------------------------------------------------------------- LIFECYCLE

    created()
    {
        if(['MD', 'UA'].includes(this.cEntity.country))
        {
            this.$router.push({ name: 'home', params: this.$route.params });
        }

        // Build chains
        this.setupDispatchQueue();
        this.setupAddToReturnBasketQueue();

        // Set data from URL
        this.lensReturnUrlManager = new LensReturnUrlManager(this.$route);
        this.orderIdentifier = this.lensReturnUrlManager.getOrderIdentifier();
        this.returnBothLenses = this.lensReturnUrlManager.getReturnBothLenses();

        if(this.orderIdentifier != '' && this.orderIdentifier != null)
        {
            console.log('A lens order identifier has been given at creation', this.orderIdentifier);

            this.dispatch();
        }
        else
        {
            this.orderIdentifier = this.$route.query.order_identifier ?? null;
            this.returnBothLenses = this.$route.query.return_both_lenses ?? null;

            console.log('No lens order identifier given at creation or only in query params.');
        }
    },

    unmounted()
    {
        delete this.$route.params.lenses; // IMPORTANT
        this.$store.commit('lensReturn/reset'); // reset both sides
    },

    // ------------------------------------------------------------------------- METHODS

    methods:
    {
        displayReturnForm()
        {
            if(this.orderIdentifier != '' && this.orderIdentifier != null)
            {
                this.lensReturnUrlManager.setOrderIdentifier(this.orderIdentifier);
                this.lensReturnUrlManager.setReturnBothLenses(this.returnBothLenses);
                this.$router.push(this.lensReturnUrlManager.getPath());
            }
            else
            {
                this.orderIdentifierFormError = this.$t('return.form.error.empty');
            }
        },

        displayOrderIdentifierForm()
        {
            const params = this.$route.params;

            this.$router.push({
                name: 'return-lenses',
                params:
                {
                    lang:      params.lang,
                    entity_id: params.entity_id,
                    lenses:    null,
                },
                query:
                {
                    order_identifier:   this.orderIdentifier,
                    return_both_lenses: this.returnBothLenses,
                },
            });
        },

        // --------------------------------------------------------------------- Helpers

        setReturn(ret)
        {
            // Avoid putting same lens on both side.
            if(ret.order.reference == this.rightReturn?.order.reference || ret.order.reference == this.leftReturn?.order.reference)
            {
                return;
            }

            // Set return on the correct side (left or right).
            let side = get(ret.order, 'article_data.LENS_SIDE', 'right');

            // Trick for "single" side lenses.
            side = (['left', 'right'].indexOf(side) == -1) ? 'right' : side;

            if(side == 'left' && !this.rightReturn)
            {
                this.rightReturn = ret;
            }
            else if(side == 'left' && this.rightReturn)
            {
                this.leftReturn = this.rightReturn;
                this.rightReturn = ret;
            }
            else if(side == 'right' && !this.leftReturn)
            {
                this.leftReturn = ret;
            }
            else if(side == 'right' && this.leftReturn)
            {
                this.rightReturn = this.leftReturn;
                this.leftReturn = ret;
            }
        },

        closeEntitySwitchAndReset()
        {
            this.leftReturn = null;
            this.rightReturn = null;
            this.showChooseEntityModal = false;
        },

        // --------------------------------------------------------------------- chain : dispatch

        async dispatch(from = null)
        {
            console.log(`DISPATCH START! (from: ${from})`);

            let firstHandlerFound = false;

            // If no "from" has been given.
            if(!from)
            {
                // Set first key as "from".
                from = dispatchQueue.keys().next().value;
            }

            this.loading = true;

            // Loop through all dispatch handlers
            for(const [key, handler] of dispatchQueue.entries())
            {
                if(key.includes(from))
                {
                    firstHandlerFound = true;
                }

                if(firstHandlerFound)
                {
                    console.groupCollapsed('Handler: ' + key);

                    try
                    {
                        const result = await handler.handle();

                        if(result === false)
                        {
                            console.groupEnd();
                            console.log('DISPATCH BREAK!');
                            break;
                        }
                    }
                    catch(error)
                    {
                        console.groupEnd();
                        console.log('DISPATCH BREAK!', error);
                        this.dispatchError = error;
                        break;
                    }

                    console.groupEnd();
                }
            }

            this.loading = false;

            console.log('DISPATCH END!');
        },

        // --------------------------------------------------------------------- Chain : save

        async save()
        {
            console.log('SAVE START!');

            this.$store.commit('overlay/setVisibility', true);

            for(const [key, handler] of addToReturnBasketQueue.entries())
            {
                console.groupCollapsed('Handler: ' + key);

                try
                {
                    const result = await handler.handle();

                    if(result === false)
                    {
                        console.groupEnd();
                        console.log('SAVE BREAK!');
                        break;
                    }
                }
                catch(error)
                {
                    console.groupEnd();
                    console.log('SAVE BREAK!', error);
                    break;
                }

                console.groupEnd();
            }

            this.$store.commit('overlay/setVisibility', false);

            console.log('SAVE END!');
        },

        // --------------------------------------------------------------------- template

        template(paramCode, paramValue, side)
        {
            console.log('LensReturn', 'update', paramCode, paramValue, side);

            if(side == 'both')
            {
                this.templateBothSides();
            }
            else
            {
                this.templateOneSide(side);
            }
        },

        templateBothSides()
        {
            const leftReturn = this.$store.getters['lensReturn/getReturn']('left');
            const rightReturn = this.$store.getters['lensReturn/getReturn']('right');

            Promise.all([
                leftReturn.template(),
                rightReturn.template(),
            ]).then(() =>
            {
                this.formDual.setParameters('left', leftReturn.getReturnQuestionnaire().getParameters());
                this.formDual.setParameters('right', rightReturn.getReturnQuestionnaire().getParameters());
                this.formDual.render();
            });
        },

        templateOneSide(side)
        {
            const cReturn = this.$store.getters['lensReturn/getReturn'](side);

            cReturn.template().then(() =>
            {
                this.formDual.setParameters(side, cReturn.getReturnQuestionnaire().getParameters());
                this.formDual.render();
            });
        },

        // --------------------------------------------------------------------- Setup chains

        setupDispatchQueue()
        {
            dispatchQueue.set('ResetErrors',                        new ResetErrors(this));
            dispatchQueue.set('FetchOrderByReference',              new FetchOrderByReference(this));
            dispatchQueue.set('FetchOrderByLensSn',                 new FetchOrderByLensSn(this));
            dispatchQueue.set('FetchOrderByLensOemNumber',          new FetchOrderByLensOemNumber(this));
            dispatchQueue.set('CheckReturnOrStop',                  new CheckReturnOrStop(this));
            dispatchQueue.set('ValidateReturnEntitiesConsistency',  new ValidateReturnEntitiesConsistency(this));
            dispatchQueue.set('FetchPairedOrderByReference',        new FetchPairedOrderByReference(this));
            dispatchQueue.set('CheckNotAlreadyReturned',            new CheckNotAlreadyReturned(this));
            dispatchQueue.set('CheckAlreadyShipped',                new CheckAlreadyShipped(this));
            dispatchQueue.set('TemplateReturnQuestionnaire-left',   new TemplateReturnQuestionnaire(this, 'left'));
            dispatchQueue.set('TemplateReturnQuestionnaire-right',  new TemplateReturnQuestionnaire(this, 'right'));
            dispatchQueue.set('SetReturnQuestionnaireValues-left',  new SetReturnQuestionnaireValues(this, 'left'));
            dispatchQueue.set('SetReturnQuestionnaireValues-right', new SetReturnQuestionnaireValues(this, 'right'));
            dispatchQueue.set('CreateFormDual',                     new CreateFormDual(this));
            dispatchQueue.set('LinkReturnQuestionnairesToFormDual', new LinkReturnQuestionnairesToFormDual(this));
            dispatchQueue.set('RenderFormDual',                     new RenderFormDual(this));
            // dispatchQueue.set('CompleteGroupedParameterValues',     new SetUrlManagerParameter(this));
        },

        setupAddToReturnBasketQueue()
        {
            addToReturnBasketQueue.set('ValidateReturns',               new ValidateReturns(this));
            addToReturnBasketQueue.set('PriceReturns',                  new PriceReturns(this));
            addToReturnBasketQueue.set('CreateReturns',                 new CreateReturns(this));
            addToReturnBasketQueue.set('NavigateToReturnConfirmation',  new NavigateToReturnConfirmation(this));
            // addToReturnBasketQueue.set('SetReturnPairId',           new SetReturnPairId(this));
            // addToReturnBasketQueue.set('AddToReturnBasket',         new AddToReturnBasket(this));
            // addToReturnBasketQueue.set('NavigateToBasket',          new NavigateToBasket(this));
        },
    },
};

</script>

<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style lang="scss">

</style>
